import type { ApolloQueryResult } from '@apollo/client';
import { useLocation } from '@gatsbyjs/reach-router';
import { Box, Container, Grid2, Link, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link as RouterLink, navigate } from 'gatsby';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { Breadcrumb } from '@components/Breadcrumb';
import Icon from '@components/Icon/Icon';
import ManageAccountMenu from '@components/ManageAccountMenu';
import ProfileCard from '@components/ProfileCard';

import { ManageAccountMenuRoute } from '@constants/manage-account';
import { MANAGE_ACCOUNT } from '@constants/routes';

import { useGetAccount } from '@hooks/api/account';
import { useGetUser } from '@hooks/api/user';
import { useIsMobile } from '@hooks/useIsMobile';
import { useSession } from '@hooks/useSession';

import type { ManageAccountModel } from '@models/account';
import type { Profile } from '@models/session';

import { formatName } from '@utils/format';
import { mapProfile } from '@utils/profiles';
import { isClient, isDependant, isPatient } from '@utils/role';

import MainWrapper from '@views/Records/Common/MainWrapper';

import { AccountType, type GetUserInput } from 'generated/types';
import type { GetUserRecordQuery } from 'generated/user.generated';

interface ManageAccountLayoutChildrenProps {
  user_id: string;
  loading: boolean;
  refetch: (
    variables?:
      | {
          input: GetUserInput;
        }
      | undefined
  ) => Promise<ApolloQueryResult<GetUserRecordQuery>>;
  user?: Profile;
  account?: ManageAccountModel;
}

interface ManageAccountLayoutProps {
  pageProps: PagePropsWithBreadcrumbs;
  friendlyName: string;
  children?: (props: ManageAccountLayoutChildrenProps) => React.ReactNode;
}

const messages = defineMessages({
  dependant: {
    id: 'GLOBAL.dependant',
    defaultMessage: 'Dependant',
  },
  primaryUser: {
    id: 'GLOBAL.primaryUser',
    defaultMessage: 'Primary User',
  },
  accountHolder: {
    id: 'GLOBAL.accountHolder',
    defaultMessage: 'Account holder',
  },
  patient: {
    id: 'GLOBAL.patient',
    defaultMessage: 'Patient',
  },
  nonPatient: {
    id: 'GLOBAL.nonPatient',
    defaultMessage: 'Non-Patient',
  },
  title: {
    id: 'manageAccount.title',
    defaultMessage: 'User Profile',
  },
  backTo: {
    id: 'manageAccount.backTo',
    defaultMessage: 'Back to User Profile',
  },
});

export default function ManageAccountLayout({
  children,
  friendlyName,
  pageProps: { pageContext },
}: ManageAccountLayoutProps) {
  const {
    breadcrumb: { crumbs },
  } = pageContext;

  const location = useLocation();

  const { formatMessage } = useIntl();
  const {
    state: { activeProfile },
  } = useSession();

  const user_id = activeProfile?.user_id ?? '';

  const isMobile = useIsMobile();

  const { palette } = useTheme();

  const {
    data: userData,
    loading: userLoading,
    refetch,
  } = useGetUser({ id: user_id });
  const { data: accountData, loading: accountLoading } = useGetAccount();
  const user = mapProfile(userData?.getUserRecord);
  const account = accountData?.getAccountRecord;
  const isOwner = accountData?.getAccountRecord.owner_id === user_id;
  const loading = accountLoading || userLoading;

  const tab = Object.keys(ManageAccountMenuRoute).find((key) =>
    `${location.pathname}/`.includes(key)
  );

  const handleTabChange = (route: ManageAccountMenuRoute) => {
    navigate(route);
  };
  const dataKey = JSON.stringify(user?.roles);
  const subtitle = React.useMemo(() => {
    if (!activeProfile) {
      return '';
    }
    if (
      account?.account_type === AccountType.organization &&
      !isClient(activeProfile)
    ) {
      return account.account_name;
    }
    const label = [
      ...(isOwner ? [formatMessage(messages.accountHolder)] : []),
      ...[
        isPatient(activeProfile)
          ? `${
              isDependant(activeProfile)
                ? formatMessage(messages.dependant)
                : ''
            } ${formatMessage(messages.patient)}`
          : formatMessage(messages.nonPatient),
      ],
    ].join(', ');
    return label;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataKey, account?.account_name, account?.account_type]);

  const showMenu = !isMobile || !tab;

  return (
    <MainWrapper
      friendlyName={crumbs.at(-1)?.crumbLabel ?? friendlyName}
      title={formatMessage(messages.title)}
    >
      <Container maxWidth="xl">
        <Grid2 container spacing={3}>
          <Grid2
            size={{
              md: 4,
              xs: 12,
            }}
          >
            <Stack gap={2}>
              <ProfileCard
                loading={loading}
                name={formatName(
                  activeProfile?.first_name,
                  activeProfile?.last_name
                )}
                profile_picture={activeProfile?.profile_picture}
                subtitle={subtitle}
              />
              {!showMenu ? (
                <Link
                  color={palette.text.secondary}
                  component={RouterLink}
                  to={MANAGE_ACCOUNT}
                  underline="always"
                  variant="subtitle2"
                  alignItems="center"
                >
                  <Stack direction="row" gap={1}>
                    <Icon name="arrow-left" type="fas" />
                    <span>{formatMessage(messages.backTo)}</span>
                  </Stack>
                </Link>
              ) : null}
              {showMenu ? (
                <ManageAccountMenu
                  user={user}
                  onSelect={handleTabChange}
                  selectedItem={tab}
                />
              ) : null}
            </Stack>
          </Grid2>
          {user_id && children ? (
            <Grid2
              size={{
                md: 8,
                xs: 12,
              }}
            >
              <Stack gap={0} spacing={0}>
                <Breadcrumb crumbs={crumbs} startIndex={2} />
                <Box>
                  {children({
                    account,
                    loading,
                    refetch,
                    user: user ?? undefined,
                    user_id,
                  })}
                </Box>
              </Stack>
            </Grid2>
          ) : null}
        </Grid2>
      </Container>
    </MainWrapper>
  );
}
